import { useMemo } from 'react';
import { TableRow } from '../types';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import { AvailableModel } from '../../../api/prediction-engine';
import {
  LC2_CAST_RULE_VERSION,
  LC2_MAX_ACTORS,
  LC2_MAX_STARS,
} from '../constants/castLimits';

// Actors and Stars cells that are full of names look fine at a glance, so a
// red border alone does not say what is wrong with them. The LC2 limit
// messages are therefore also kept under their own keys: one per cell, shown
// as text in it, and a summary for the Process button. The cell's own key can
// hold the "required" message as well, which the cells do not spell out.
export const CAST_LIMIT_ERROR_KEY = 'cast_limit';
export const castLimitErrorKey = (fieldKey: string): string =>
  `${fieldKey}_limit`;

export interface ValidationErrors {
  [rowIndex: number]: {
    [fieldKey: string]: string | boolean;
  };
}

export interface ValidationResult {
  validationErrors: ValidationErrors;
  hasValidationErrors: boolean;
  hasEmptyRows: boolean;
  isRowValid: (rowIndex: number) => boolean;
  getFieldError: (rowIndex: number, fieldKey: string) => string | boolean | undefined;
}

const isDateValid = (dateString: string, limitDate: string): boolean => {
  if (!dateString || !limitDate) return true;
  return new Date(dateString) >= new Date(limitDate);
};

const validateRequiredField = (value: string | string[] | any): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value && value.toString().trim());
};

const validateAtLeastOneRole = (
  directors: string[] | any,
  writers: string[] | any,
  producers: string[] | any
): boolean => {
  const hasDirector = Array.isArray(directors) && directors.length > 0;
  const hasWriter = Array.isArray(writers) && writers.length > 0;
  const hasProducer = Array.isArray(producers) && producers.length > 0;
  
  return hasDirector || hasWriter || hasProducer;
};

const validateAtLeastOnePerformer = (
  actors: string[] | any,
  stars: string[] | any
): boolean => {
  const hasActor = Array.isArray(actors) && actors.length > 0;
  const hasStar = Array.isArray(stars) && stars.length > 0;

  return hasActor || hasStar;
};

const isRowEmpty = (row: TableRow): boolean => {
  const checkFields = [
    LOAD_TABLE_FIELDS.internal_id,
    LOAD_TABLE_FIELDS.title,
    LOAD_TABLE_FIELDS.release_date,
    LOAD_TABLE_FIELDS.budget,
    LOAD_TABLE_FIELDS.actors,
    LOAD_TABLE_FIELDS.directors,
    LOAD_TABLE_FIELDS.writers,
    LOAD_TABLE_FIELDS.producers,
    LOAD_TABLE_FIELDS.stars,
    LOAD_TABLE_FIELDS.plot,
    LOAD_TABLE_FIELDS.mpaa_rating,
    LOAD_TABLE_FIELDS.runtime,
    LOAD_TABLE_FIELDS.aspect_ratio,
    LOAD_TABLE_FIELDS.genres,
  ];

  return checkFields.every((key) => {
    const value = row[key as keyof TableRow];
    if (Array.isArray(value)) return value.length === 0;
    return !value || String(value).trim() === '';
  });
};

export const useFormValidation = (
  tableData: TableRow[],
  selectedModel: AvailableModel | null
): ValidationResult => {
  const validationErrors = useMemo(() => {
    const errors: ValidationErrors = {};
    
    tableData.forEach((row, rowIndex) => {
      if (isRowEmpty(row)) return;

      const rowErrors: { [fieldKey: string]: string | boolean } = {};

      // Required field validations
      const requiredFields = [
        { key: LOAD_TABLE_FIELDS.title, label: 'Title' },
        { key: LOAD_TABLE_FIELDS.release_date, label: 'Release Date' },
        { key: LOAD_TABLE_FIELDS.budget, label: 'Budget' },
        { key: LOAD_TABLE_FIELDS.mpaa_rating, label: 'MPAA Rating' },
        { key: LOAD_TABLE_FIELDS.runtime, label: 'Runtime' },
        { key: LOAD_TABLE_FIELDS.aspect_ratio, label: 'Aspect Ratio' },
        { key: LOAD_TABLE_FIELDS.genres, label: 'Genres' },
      ];
      
      requiredFields.forEach(({ key, label }) => {
        if (!validateRequiredField(row[key])) {
          rowErrors[key] = `${label} is required`;
        }
      });
      
      // Release date limit validation (existing logic)
      if (selectedModel?.predicting_limit_date) {
        const releaseDate = row[LOAD_TABLE_FIELDS.release_date] as string;
        if (releaseDate && !isDateValid(releaseDate, selectedModel.predicting_limit_date)) {
          rowErrors[LOAD_TABLE_FIELDS.release_date] = 'Release date must be after the model limit date';
        }
      }
      
      // At least one Director/Writer/Producer validation
      if (!validateAtLeastOneRole(
        row[LOAD_TABLE_FIELDS.directors],
        row[LOAD_TABLE_FIELDS.writers],
        row[LOAD_TABLE_FIELDS.producers]
      )) {
        const crewMessage = 'At least one Director, Writer, or Producer is required';
        rowErrors['crew_required'] = crewMessage;
        // Mark individual fields with specific error messages
        rowErrors[LOAD_TABLE_FIELDS.directors] = crewMessage;
        rowErrors[LOAD_TABLE_FIELDS.writers] = crewMessage;
        rowErrors[LOAD_TABLE_FIELDS.producers] = crewMessage;
      }
      
      // At least one Actor/Star validation
      if (!validateAtLeastOnePerformer(
        row[LOAD_TABLE_FIELDS.actors],
        row[LOAD_TABLE_FIELDS.stars]
      )) {
        const castMessage = 'At least one Actor or Star is required';
        rowErrors['cast_required'] = castMessage;
        // Mark individual fields with specific error messages
        rowErrors[LOAD_TABLE_FIELDS.actors] = castMessage;
        rowErrors[LOAD_TABLE_FIELDS.stars] = castMessage;
      }

      // LC2 cast limits. Every entry under Actors counts, stars included.
      if (selectedModel?.cast_rule_version === LC2_CAST_RULE_VERSION) {
        const actors = row[LOAD_TABLE_FIELDS.actors];
        const stars = row[LOAD_TABLE_FIELDS.stars];
        const tooManyActors =
          Array.isArray(actors) && actors.length > LC2_MAX_ACTORS;
        const tooManyStars =
          Array.isArray(stars) && stars.length > LC2_MAX_STARS;

        if (tooManyActors) {
          const message = `This model uses at most ${LC2_MAX_ACTORS} actors (a star listed under Actors counts). Remove ${actors.length - LC2_MAX_ACTORS}.`;
          rowErrors[LOAD_TABLE_FIELDS.actors] = message;
          rowErrors[castLimitErrorKey(LOAD_TABLE_FIELDS.actors)] = message;
        }
        if (tooManyStars) {
          const message = `This model uses at most ${LC2_MAX_STARS} stars. Remove ${stars.length - LC2_MAX_STARS}.`;
          rowErrors[LOAD_TABLE_FIELDS.stars] = message;
          rowErrors[castLimitErrorKey(LOAD_TABLE_FIELDS.stars)] = message;
        }
        if (tooManyActors || tooManyStars) {
          rowErrors[CAST_LIMIT_ERROR_KEY] =
            `This model uses at most ${LC2_MAX_ACTORS} actors and ${LC2_MAX_STARS} stars per film (a star listed under Actors counts). Remove the extra names from the cells marked in red.`;
        }
      }

      // Only add to errors object if there are actual errors
      if (Object.keys(rowErrors).length > 0) {
        errors[rowIndex] = rowErrors;
      }
    });
    
    return errors;
  }, [
    tableData,
    selectedModel?.predicting_limit_date,
    selectedModel?.cast_rule_version,
  ]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const hasEmptyRows = tableData.some(isRowEmpty);

  const isRowValid = (rowIndex: number): boolean => {
    return !validationErrors[rowIndex];
  };

  const getFieldError = (rowIndex: number, fieldKey: string): string | boolean | undefined => {
    return validationErrors[rowIndex]?.[fieldKey];
  };

  return {
    validationErrors,
    hasValidationErrors,
    hasEmptyRows,
    isRowValid,
    getFieldError,
  };
};